import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDirectionContext } from '../contexts/DirectionContext';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    FormControlLabel,
    Typography,
    Card,
    CardContent,
    Chip,
    IconButton,
    Tooltip,
    Box,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Button,
    Collapse,
    Skeleton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LaunchIcon from '@mui/icons-material/Launch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { format, parseISO, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { colors, spacing, typography, shadows, radius, transitions } from '../theme/tokens';

// Styled components using brand colors
const BrandCard = styled(Card)({
    borderRadius: '16px',
    boxShadow: 'rgba(26, 0, 0, 0.1) 0 4px 16px',
    border: '1px solid #EAEEF2',
    backgroundColor: '#ffffff',
    transition: 'all 0.3s ease',
    '&:hover': {
        // Use filter instead of boxShadow for better performance (GPU-accelerated)
        filter: 'drop-shadow(0 6px 20px rgba(26, 0, 0, 0.15))',
        transform: 'translateY(-2px)',
    }
});

const StyledTableContainer = styled(TableContainer)({
    borderRadius: '12px',
    border: 'none',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    backgroundColor: '#ffffff',
    overflow: 'hidden'
});

const StyledTableHead = styled(TableHead)({
    backgroundColor: '#283593 !important',
    background: 'linear-gradient(180deg, #303f9f 0%, #283593 100%) !important',
    '& .MuiTableCell-head': {
        backgroundColor: 'transparent !important',
        color: '#ffffff !important',
        fontWeight: '700 !important',
        fontSize: '14px !important',
        padding: '22px 16px !important',
        borderBottom: '3px solid #4fc3f7 !important',
        textAlign: 'center !important',
        whiteSpace: 'nowrap !important',
        verticalAlign: 'middle !important',
        boxShadow: 'inset 0 -3px 0 0 rgba(255, 255, 255, 0.1) !important',
        '& svg': {
            color: '#4fc3f7 !important',
            fontSize: '32px !important',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3)) !important'
        },
        '& span': {
            color: '#ffffff !important',
            fontWeight: '700 !important',
            display: 'block !important',
            textShadow: '0 1px 3px rgba(0,0,0,0.3) !important'
        }
    }
});

const StyledTableRow = styled(TableRow)(({ theme, accepted }) => ({
    transition: 'all 0.3s ease',
    backgroundColor: accepted ? 'rgba(106, 179, 235, 0.08)' : 'rgba(239, 68, 68, 0.08)',
    borderRight: `5px solid ${accepted ? colors.secondary.main : '#ef4444'}`,
    '&:hover': {
        backgroundColor: accepted ? 'rgba(106, 179, 235, 0.15)' : 'rgba(239, 68, 68, 0.15)',
        transform: 'translateX(-4px)',
        // Use filter instead of boxShadow for better performance (GPU-accelerated)
        filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1))',
    },
    '& .MuiTableCell-root': {
        padding: '18px 12px',
        borderBottom: '2px solid #f0f2f5',
        fontSize: '14px',
        fontWeight: '600',
        color: '#2d3748'
    }
}));

const StatusChip = styled(Chip)(({ accepted }) => ({
    fontWeight: '600',
    fontSize: '12px',
    height: '28px',
    borderRadius: '14px',
    backgroundColor: accepted ? '#e3f2fd' : '#fef2f2',
    color: accepted ? colors.secondary.main : '#ef4444',
    border: `1px solid ${accepted ? colors.secondary.main : '#ef4444'}`,
    '& .MuiChip-icon': {
        color: accepted ? colors.secondary.main : '#ef4444'
    }
}));

const StyledCheckbox = styled(Checkbox)({
    color: colors.primary.main,
    '&.Mui-checked': {
        color: colors.secondary.main,
    },
    '&:hover': {
        backgroundColor: 'rgba(19, 36, 161, 0.1)',
    }
});

const DocumentLink = styled('a')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: colors.primary.main,
    color: '#ffffff',
    textDecoration: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&:hover': {
        backgroundColor: colors.primary.dark,
        transform: 'translateY(-2px)',
        // Use filter instead of boxShadow for better performance (GPU-accelerated)
        filter: 'drop-shadow(0 4px 12px rgba(26, 0, 0, 0.15))'
    }
});

const LoadingOverlay = styled('div')({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const LoadingCard = styled(BrandCard)({
    padding: '40px',
    textAlign: 'center',
    maxWidth: '400px',
    margin: '20px',
});

const StatCard = styled(BrandCard)({
    padding: '24px',
    textAlign: 'center',
    minWidth: '160px'
});

const FilterCard = styled(BrandCard)({
    marginBottom: '24px',
    border: `2px solid ${colors.primary.main}`,
    '& .MuiCardContent-root': {
        padding: '24px !important'
    }
});

const PageContainer = styled('div')({
    minHeight: '100vh',
    backgroundColor: colors.background.secondary,
    direction: 'rtl',
    padding: spacing.lg,
    fontFamily: typography.fontFamily
});

const ContentWrapper = styled('div')({
    maxWidth: '1400px',
    margin: '0 auto'
});

const HeaderCard = styled(BrandCard)({
    marginBottom: spacing.xl
});

const HeaderContent = styled(CardContent)({
    padding: `${spacing.xl} !important`
});

const HeaderRow = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.lg
});

const TitleWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md
});

const PageTitle = styled(Typography)({
    fontWeight: typography.fontWeight.bold,
    color: colors.text.primary,
    marginBottom: spacing.xs
});

const Subtitle = styled(Typography)({
    color: colors.text.secondary,
    fontWeight: typography.fontWeight.medium
});

const StatValue = styled(Typography)({
    fontWeight: typography.fontWeight.bold,
    marginBottom: spacing.xs
});

const StatLabel = styled(Typography)({
    color: colors.text.secondary,
    fontWeight: typography.fontWeight.semibold
});

const StatNote = styled(Typography)({
    color: colors.text.tertiary,
    display: 'block',
    marginTop: spacing.xxs
});

const FilterToggleRow = styled('div')(({ showFilters }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: showFilters ? spacing.lg : 0
}));

const FilterTitle = styled(Typography)({
    fontWeight: typography.fontWeight.semibold,
    color: colors.primary.main,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs
});

const ClearFiltersWrapper = styled('div')(({ direction }) => ({
    marginTop: spacing.md,
    textAlign: direction === 'rtl' ? 'right' : 'left'
}));

const TableHeaderCell = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    padding: spacing.xs + ' 0'
});

const TableHeaderLabel = styled('span')({
    fontSize: '13px',
    fontWeight: typography.fontWeight.bold,
    color: '#ffffff',
    lineHeight: 1.3,
    letterSpacing: '0.3px'
});

const StatusColumn = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing.xs
});

const DateText = styled(Typography)({
    fontWeight: typography.fontWeight.semibold,
    color: colors.text.primary,
    direction: 'ltr'
});

const TimeText = styled(Typography)({
    color: colors.text.secondary,
    display: 'block',
    direction: 'ltr'
});

const CellText = styled(Typography)({
    fontWeight: typography.fontWeight.semibold,
    color: colors.text.primary
});

const MonoText = styled(Typography)({
    fontWeight: typography.fontWeight.semibold,
    color: colors.text.primary,
    fontFamily: 'monospace'
});

const PhoneText = styled(Typography)({
    fontWeight: typography.fontWeight.medium,
    color: colors.primary.main,
    direction: 'ltr'
});

const EmptyStateContainer = styled(Box)({
    padding: '60px 20px',
    textAlign: 'center',
    backgroundColor: colors.background.paper
});

const EmptyStateIcon = styled('div')(({ theme }) => ({
    fontSize: '64px',
    color: colors.text.disabled,
    marginBottom: spacing.md
}));

const EmptyStateTitle = styled(Typography)({
    color: colors.text.secondary,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing.xs
});

const EmptyStateText = styled(Typography)({
    color: colors.text.disabled,
    marginBottom: spacing.md
});

const FooterCard = styled(BrandCard)({
    marginTop: spacing.lg
});

const FooterText = styled(Typography)({
    color: colors.text.secondary,
    textAlign: 'center',
    fontWeight: typography.fontWeight.medium
});

export default function Submitted(props) {
    const { t } = useTranslation();
    const direction = useDirectionContext();
    const [data, setData] = useState(false);
    const [loading, setLoading] = useState(false);
    const [update, setUpdate] = useState(0);

    // Filter and search states
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState({
        from: '',
        to: ''
    });
    const [showFilters, setShowFilters] = useState(false);

    const navigate = useNavigate();
    const { key } = useParams();

    useEffect(() => {
        const fetchSubmittedData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${window.AppConfig.serverDomain}/api/organzations/submitted/${key}`);
                const result = response.data.map((submitted) => {
                    const { accepted, staticFields, signedPdf, submitterPhone, updatedAt, createdAt, submittionID } = submitted;
                    const { childId, childName, parentName1, parentName2 } = staticFields;
                    return { updatedAt, createdAt, accepted, childId, childName, submitterPhone, parentName1, parentName2, signedPdf, submittionID };
                });

                console.log(result);
                setData(result);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
            }
        };

        fetchSubmittedData();
    }, [update, key]);

    // Filtered and searched data
    const filteredData = useMemo(() => {
        if (!data || !Array.isArray(data)) return [];

        let filtered = [...data];

        // Search filter
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase().trim();
            filtered = filtered.filter(row =>
                (row.childName && row.childName.toLowerCase().includes(searchLower)) ||
                (row.childId && row.childId.toLowerCase().includes(searchLower)) ||
                (row.parentName1 && row.parentName1.toLowerCase().includes(searchLower)) ||
                (row.parentName2 && row.parentName2.toLowerCase().includes(searchLower)) ||
                (row.submitterPhone && row.submitterPhone.includes(searchLower))
            );
        }

        // Status filter
        if (statusFilter !== 'all') {
            const isAccepted = statusFilter === 'approved';
            filtered = filtered.filter(row => row.accepted === isAccepted);
        }

        // Date range filter
        if (dateFilter.from || dateFilter.to) {
            filtered = filtered.filter(row => {
                const rowDate = new Date(row.createdAt || row.updatedAt);

                let withinRange = true;

                if (dateFilter.from) {
                    const fromDate = startOfDay(new Date(dateFilter.from));
                    withinRange = withinRange && rowDate >= fromDate;
                }

                if (dateFilter.to) {
                    const toDate = endOfDay(new Date(dateFilter.to));
                    withinRange = withinRange && rowDate <= toDate;
                }

                return withinRange;
            });
        }

        return filtered;
    }, [data, searchTerm, statusFilter, dateFilter]);

    // Statistics
    const stats = useMemo(() => {
        if (!data || !Array.isArray(data)) return { total: 0, approved: 0, pending: 0 };

        return {
            total: data.length,
            approved: data.filter(row => row.accepted).length,
            pending: data.filter(row => !row.accepted).length
        };
    }, [data]);

    const filteredStats = useMemo(() => {
        return {
            total: filteredData.length,
            approved: filteredData.filter(row => row.accepted).length,
            pending: filteredData.filter(row => !row.accepted).length
        };
    }, [filteredData]);

    const handleAcceptedChange = async (event, timestamp, key, submittionID) => {
        const localUrl = `${window.AppConfig.serverDomain}/api/organzations/update-accpeted/`;

        const formData = {
            timestamp: timestamp,
            submitter: key.split('_').pop(),
            templateID: key.split('_')[0],
            submittionID: submittionID,
            value: event.target.checked
        };

        try {
            const response = await axios.post(localUrl, formData);
            setUpdate(update + 1);
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setDateFilter({ from: '', to: '' });
    };

    const hasActiveFilters = searchTerm || statusFilter !== 'all' || dateFilter.from || dateFilter.to;

    return (
        <PageContainer>
            {loading ? (
                /* Skeleton Loading State */
                <ContentWrapper>
                    {/* Header Skeleton */}
                    <HeaderCard>
                        <HeaderContent>
                            <HeaderRow>
                                <TitleWrapper>
                                    <Skeleton variant="circular" width={32} height={32} />
                                    <Box>
                                        <Skeleton variant="text" width={200} height={40} />
                                        <Skeleton variant="text" width={150} height={28} />
                                    </Box>
                                </TitleWrapper>
                            </HeaderRow>
                        </HeaderContent>
                    </HeaderCard>

                    {/* Statistics Skeletons */}
                    <Grid container spacing={3} sx={{ marginBottom: spacing.lg }}>
                        {[1, 2, 3].map((index) => (
                            <Grid item xs={12} sm={4} key={index}>
                                <StatCard>
                                    <Skeleton variant="text" width={80} height={60} sx={{ margin: '0 auto' }} />
                                    <Skeleton variant="text" width={120} height={20} sx={{ margin: '0 auto' }} />
                                </StatCard>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Table Skeleton */}
                    <FilterCard>
                        <CardContent>
                            <Skeleton variant="rounded" width="100%" height={56} />
                        </CardContent>
                    </FilterCard>

                    <StyledTableContainer>
                        <Table>
                            <StyledTableHead>
                                <TableRow>
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map((col) => (
                                        <TableCell key={col} align="center">
                                            <Skeleton variant="text" width="100%" height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </StyledTableHead>
                            <TableBody>
                                {[1, 2, 3, 4, 5].map((row) => (
                                    <TableRow key={row}>
                                        {[1, 2, 3, 4, 5, 6, 7, 8].map((col) => (
                                            <TableCell key={col} align="center">
                                                <Skeleton variant="text" width="80%" height={24} sx={{ margin: '0 auto' }} />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                </ContentWrapper>
            ) : data && (
                <ContentWrapper>
                    {/* Header Section */}
                    <HeaderCard>
                        <HeaderContent>
                            <HeaderRow>
                                <div>
                                    <TitleWrapper>
                                        <DescriptionIcon sx={{ fontSize: '32px', color: colors.primary.main }} />
                                        <PageTitle variant="h4">
                                            {t('submitted.title')}
                                        </PageTitle>
                                    </TitleWrapper>
                                    <Subtitle variant="h6">
                                        {t('submitted.subtitle')}
                                    </Subtitle>
                                </div>
                            </HeaderRow>
                        </HeaderContent>
                    </HeaderCard>

                    {/* Statistics Cards */}
                    <Grid container spacing={3} sx={{ marginBottom: spacing.lg }}>
                        <Grid item xs={12} sm={4}>
                            <StatCard>
                                <StatValue variant="h3" sx={{ color: colors.primary.main }}>
                                    {filteredStats.total}
                                </StatValue>
                                <StatLabel variant="body2">
                                    {t('submitted.totalSubmissions')}
                                </StatLabel>
                                {hasActiveFilters && (
                                    <StatNote variant="caption">
                                        {t('submitted.outOf', { total: stats.total })}
                                    </StatNote>
                                )}
                            </StatCard>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <StatCard>
                                <StatValue variant="h3" sx={{ color: colors.secondary.main }}>
                                    {filteredStats.approved}
                                </StatValue>
                                <StatLabel variant="body2">
                                    {t('submitted.approved')}
                                </StatLabel>
                            </StatCard>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <StatCard>
                                <StatValue variant="h3" sx={{ color: '#ef4444' }}>
                                    {filteredStats.pending}
                                </StatValue>
                                <StatLabel variant="body2">
                                    {t('submitted.pending')}
                                </StatLabel>
                            </StatCard>
                        </Grid>
                    </Grid>

                    {/* Filters Section */}
                    <FilterCard>
                        <CardContent>
                            {/* Filter Toggle */}
                            <FilterToggleRow showFilters={showFilters}>
                                <FilterTitle variant="h6">
                                    <FilterListIcon />
                                    {t('submitted.searchAndFilter')}
                                </FilterTitle>
                                <Button
                                    onClick={() => setShowFilters(!showFilters)}
                                    sx={{
                                        color: colors.primary.main,
                                        fontWeight: typography.fontWeight.semibold
                                    }}
                                    endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                >
                                    {showFilters ? t('submitted.hideFilters') : t('submitted.showFilters')}
                                </Button>
                            </FilterToggleRow>

                            {/* Search Bar - Always Visible */}
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder={t('submitted.searchPlaceholder')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon style={{ color: '#666666' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setSearchTerm('')}
                                                size="small"
                                                style={{ color: '#666666' }}
                                            >
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    style: {
                                        backgroundColor: '#ffffff',
                                        borderRadius: '8px'
                                    }
                                }}
                                style={{ marginBottom: showFilters ? '16px' : '0' }}
                            />

                            {/* Advanced Filters */}
                            <Collapse in={showFilters}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth variant="outlined">
                                            <InputLabel>{t('submitted.statusLabel')}</InputLabel>
                                            <Select
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                label={t('submitted.statusLabel')}
                                                style={{ backgroundColor: '#ffffff' }}
                                            >
                                                <MenuItem value="all">{t('submitted.statusAll')}</MenuItem>
                                                <MenuItem value="approved">{t('submitted.statusApproved')}</MenuItem>
                                                <MenuItem value="pending">{t('submitted.statusPending')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            label={t('submitted.fromDate')}
                                            type="date"
                                            value={dateFilter.from}
                                            onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
                                            InputLabelProps={{ shrink: true }}
                                            style={{ backgroundColor: '#ffffff', borderRadius: '4px' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            label={t('submitted.toDate')}
                                            type="date"
                                            value={dateFilter.to}
                                            onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
                                            InputLabelProps={{ shrink: true }}
                                            style={{ backgroundColor: '#ffffff', borderRadius: '4px' }}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Clear Filters Button */}
                                {hasActiveFilters && (
                                    <ClearFiltersWrapper direction={direction}>
                                        <Button
                                            onClick={handleClearFilters}
                                            variant="outlined"
                                            startIcon={<ClearIcon />}
                                            sx={{
                                                color: '#ef4444',
                                                borderColor: '#ef4444'
                                            }}
                                        >
                                            {t('submitted.clearFilters')}
                                        </Button>
                                    </ClearFiltersWrapper>
                                )}
                            </Collapse>
                        </CardContent>
                    </FilterCard>

                    {/* Main Table */}
                    <StyledTableContainer>
                        <Table stickyHeader>
                            <StyledTableHead>
                                <TableRow>
                                    <TableCell align="center">
                                        <TableHeaderCell>
                                            <CheckCircleIcon />
                                            <TableHeaderLabel>{t('submitted.approvalStatus')}</TableHeaderLabel>
                                        </TableHeaderCell>
                                    </TableCell>
                                    <TableCell align="center">
                                        <TableHeaderCell>
                                            <CalendarTodayIcon />
                                            <TableHeaderLabel>{t('submitted.submissionTime')}</TableHeaderLabel>
                                        </TableHeaderCell>
                                    </TableCell>
                                    <TableCell align="center">
                                        <TableHeaderCell>
                                            <BadgeIcon />
                                            <TableHeaderLabel>{t('submitted.childId')}</TableHeaderLabel>
                                        </TableHeaderCell>
                                    </TableCell>
                                    <TableCell align="center">
                                        <TableHeaderCell>
                                            <PersonIcon />
                                            <TableHeaderLabel>{t('submitted.childName')}</TableHeaderLabel>
                                        </TableHeaderCell>
                                    </TableCell>
                                    <TableCell align="center">
                                        <TableHeaderCell>
                                            <PhoneIcon />
                                            <TableHeaderLabel>{t('submitted.signerNumber')}</TableHeaderLabel>
                                        </TableHeaderCell>
                                    </TableCell>
                                    <TableCell align="center">
                                        <TableHeaderCell>
                                            <PersonIcon />
                                            <TableHeaderLabel>{t('submitted.parent1')}</TableHeaderLabel>
                                        </TableHeaderCell>
                                    </TableCell>
                                    <TableCell align="center">
                                        <TableHeaderCell>
                                            <PersonIcon />
                                            <TableHeaderLabel>{t('submitted.parent2')}</TableHeaderLabel>
                                        </TableHeaderCell>
                                    </TableCell>
                                    <TableCell align="center">
                                        <TableHeaderCell>
                                            <DescriptionIcon />
                                            <TableHeaderLabel>{t('submitted.signedDocument')}</TableHeaderLabel>
                                        </TableHeaderCell>
                                    </TableCell>
                                </TableRow>
                            </StyledTableHead>
                            <TableBody>
                                {filteredData.map((row, index) => (
                                    <StyledTableRow key={index} accepted={row.accepted}>
                                        <TableCell align="center">
                                            <StatusColumn>
                                                <StyledCheckbox
                                                    checked={row.accepted}
                                                    onChange={(event) => handleAcceptedChange(
                                                        event,
                                                        row.createdAt || row.updatedAt,
                                                        row.signedPdf.split('/').pop().split(".")[0],
                                                        row.submittionID
                                                    )}
                                                    size="medium"
                                                />
                                                <StatusChip
                                                    accepted={row.accepted}
                                                    icon={row.accepted ? <CheckCircleIcon /> : <PendingIcon />}
                                                    label={row.accepted ? t('submitted.approvedLabel') : t('submitted.pendingLabel')}
                                                    size="small"
                                                />
                                            </StatusColumn>
                                        </TableCell>
                                        <TableCell align="center">
                                            <DateText variant="body2">
                                                {format(new Date(row.createdAt || row.updatedAt), 'dd/MM/yyyy')}
                                            </DateText>
                                            <TimeText variant="caption">
                                                {format(new Date(row.createdAt || row.updatedAt), 'HH:mm:ss')}
                                            </TimeText>
                                        </TableCell>
                                        <TableCell align="center">
                                            <MonoText variant="body2">
                                                {row.childId || t('submitted.notSpecified')}
                                            </MonoText>
                                        </TableCell>
                                        <TableCell align="center">
                                            <CellText variant="body2">
                                                {row.childName || t('submitted.notSpecified')}
                                            </CellText>
                                        </TableCell>
                                        <TableCell align="center">
                                            <PhoneText variant="body2">
                                                {row.submitterPhone || t('submitted.notSpecified')}
                                            </PhoneText>
                                        </TableCell>
                                        <TableCell align="center">
                                            <CellText variant="body2">
                                                {row.parentName1 || t('submitted.notSpecified')}
                                            </CellText>
                                        </TableCell>
                                        <TableCell align="center">
                                            <CellText variant="body2">
                                                {row.parentName2 || t('submitted.notSpecified')}
                                            </CellText>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title={t('submitted.openSignedDocument')}>
                                                <DocumentLink
                                                    target="_blank"
                                                    href={`${window.AppConfig.serverDomain}/api/documentSign/${row.signedPdf.includes('/') ? row.signedPdf.split('/').pop() : row.signedPdf}`}
                                                >
                                                    <DescriptionIcon fontSize="small" />
                                                    {t('submitted.viewDocument')}
                                                    <LaunchIcon fontSize="small" />
                                                </DocumentLink>
                                            </Tooltip>
                                        </TableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>

                        {/* Empty State */}
                        {filteredData.length === 0 && (
                            <EmptyStateContainer>
                                {hasActiveFilters ? (
                                    <>
                                        <EmptyStateIcon>
                                            <SearchIcon fontSize="inherit" />
                                        </EmptyStateIcon>
                                        <EmptyStateTitle variant="h6">
                                            {t('submitted.noResultsFound')}
                                        </EmptyStateTitle>
                                        <EmptyStateText variant="body2">
                                            {t('submitted.tryChangingFilters')}
                                        </EmptyStateText>
                                        <Button
                                            onClick={handleClearFilters}
                                            variant="outlined"
                                            startIcon={<ClearIcon />}
                                            sx={{
                                                color: colors.primary.main,
                                                borderColor: colors.primary.main
                                            }}
                                        >
                                            {t('submitted.clearFilters')}
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <EmptyStateIcon>
                                            <DescriptionIcon fontSize="inherit" />
                                        </EmptyStateIcon>
                                        <EmptyStateTitle variant="h6">
                                            {t('submitted.noSubmissionsYet')}
                                        </EmptyStateTitle>
                                        <EmptyStateText variant="body2">
                                            {t('submitted.submissionsWillAppear')}
                                        </EmptyStateText>
                                    </>
                                )}
                            </EmptyStateContainer>
                        )}
                    </StyledTableContainer>

                    {/* Footer Info */}
                    {filteredData.length > 0 && (
                        <FooterCard>
                            <CardContent sx={{ padding: spacing.lg + ' !important' }}>
                                <FooterText variant="body2">
                                    {hasActiveFilters ?
                                        t('submitted.showing', { filtered: filteredData.length, total: data.length }) :
                                        t('submitted.total', { count: filteredData.length })
                                    }
                                </FooterText>
                            </CardContent>
                        </FooterCard>
                    )}
                </ContentWrapper>
            )}
        </PageContainer>
    );
}